import { useState } from "react";
import { cn } from "@/lib/utils";
import { Check, Copy, Terminal } from "lucide-react";

interface TerminalLine {
  /** The command or text to display */
  text: string;
  /** Whether this is a command (gets $ prefix and bold) or output (dimmed) */
  type?: "command" | "output" | "comment";
}

interface TerminalBlockProps {
  /** Label shown in the header (e.g. "terminal", "curl", "PowerShell") */
  label?: string;
  /** Icon shown next to the label */
  icon?: React.ReactNode;
  /** Lines to display. Strings are treated as commands. */
  lines: (string | TerminalLine)[];
  /** Text to copy when the copy button is clicked. Defaults to all command lines joined. */
  copyText?: string;
  /** Accessible name for the copy button — name what gets copied when a page shows several blocks. */
  copyLabel?: string;
  className?: string;
}

function normalizeLine(line: string | TerminalLine): TerminalLine {
  if (typeof line === "string") {
    if (line.startsWith("#")) return { text: line, type: "comment" };
    return { text: line, type: "command" };
  }
  return { type: "command", ...line };
}

/*
 * Terminal palette — intentionally fixed dark colors (not theme tokens).
 * Terminal blocks always render dark regardless of light/dark mode,
 * matching the convention used by code editors and install docs.
 * Every text colour clears WCAG AAA (7:1) against its background.
 */
const t = {
  border: "border-[hsl(25,12%,21%)]",
  headerBg: "bg-[hsl(25,14%,15%)]",
  headerText: "text-[hsl(30,12%,72%)]",
  codeBg: "bg-[hsl(25,16%,12%)]",
  command: "text-[hsl(30,30%,90%)]",
  output: "text-[hsl(30,12%,72%)]",
  comment: "text-[hsl(30,12%,68%)]",
  url: "text-[hsl(30,60%,63%)]",
  flag: "text-[hsl(205,40%,75%)]",
  pipe: "text-[hsl(30,12%,72%)]",
} as const;

export function TerminalBlock({
  label = "terminal",
  icon,
  lines,
  copyText,
  copyLabel = "Copy to clipboard",
  className,
}: TerminalBlockProps) {
  const [copied, setCopied] = useState(false);

  const normalized = lines.map(normalizeLine);

  const textToCopy =
    copyText ??
    normalized
      .filter((l) => l.type === "command")
      .map((l) => l.text)
      .join("\n");

  function handleCopy() {
    navigator.clipboard.writeText(textToCopy);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div
      className={cn("overflow-hidden rounded-xl border", t.border, className)}
    >
      {/* Header */}
      <div
        className={cn(
          "flex items-center justify-between px-4 py-2.5",
          t.headerBg,
        )}
      >
        <div className={cn("flex items-center gap-2 text-sm", t.headerText)}>
          {icon ?? <Terminal className="h-3.5 w-3.5" />}
          <span>{label}</span>
        </div>
        {textToCopy && (
          <button
            type="button"
            onClick={handleCopy}
            className={cn(
              "flex h-7 w-7 items-center justify-center rounded-md transition-colors hover:bg-white/10",
              t.headerText,
            )}
            aria-label={copied ? "Copied" : copyLabel}
          >
            {copied ? (
              <Check className="h-3.5 w-3.5 text-emerald-400" />
            ) : (
              <Copy className="h-3.5 w-3.5" />
            )}
          </button>
        )}
      </div>

      {/* Code area */}
      <div className={cn("p-4", t.codeBg)}>
        <pre className="text-sm leading-relaxed whitespace-pre-wrap break-all">
          {normalized.map((line, i) => (
            <div key={`${i}-${line.text.slice(0, 20)}`}>
              {line.type === "command" && (
                <>
                  <span className="select-none text-emerald-400">$ </span>
                  <span className={cn("font-semibold", t.command)}>
                    {highlightCommand(line.text)}
                  </span>
                </>
              )}
              {line.type === "output" && (
                <span className={t.output}>{line.text}</span>
              )}
              {line.type === "comment" && (
                <span className={t.comment}>{line.text}</span>
              )}
            </div>
          ))}
        </pre>
      </div>
    </div>
  );
}

/**
 * Simple syntax highlighting for terminal commands.
 * Matches: URLs, long flags (--flag), short flags (-f with a letter),
 * and pipe operators. Standalone `--` (argument separator) is not colored.
 */
function highlightCommand(text: string): React.ReactNode {
  // Match URLs, --long-flags, -x short flags (letter after dash), and pipe
  const parts = text.split(/(https?:\/\/\S+|--\w[\w-]*|-[a-zA-Z]\w*|[|])/g);
  return parts.map((part, i) => {
    if (part.match(/^https?:\/\//)) {
      return (
        <span key={i} className={t.url}>
          {part}
        </span>
      );
    }
    if (part === "|") {
      return (
        <span key={i} className={t.pipe}>
          {" | "}
        </span>
      );
    }
    if (part.match(/^--?\w/)) {
      return (
        <span key={i} className={t.flag}>
          {part}
        </span>
      );
    }
    return <span key={i}>{part}</span>;
  });
}
